import { V2Card, V2Button, Icon, LoadingState } from '@sakhtar/design-system/react';

/**
 * Phase C provisioning pair (C6 «آماده‌سازی نهایی» / C7 «ERP آماده است»),
 * plus the C6 failure state — the one piece of the Team/Review boards this
 * codebase explicitly deferred (see the PR description and
 * `SetupWizardPage.tsx`'s own note): `completeSetup` is a single atomic
 * `POST .../setup/complete`, not a server-reported multi-phase process, so
 * the board's own animated step-by-step checklist (`provSteps`: done /
 * running / pending rows) would be fabricated client-side state describing
 * backend progress that doesn't exist — exactly the "synthetic timers
 * misrepresenting backend state" this codebase already rejected once for
 * `TrialOnboardingPage`'s own provisioning step. This renders the same
 * three real states (running / done / failed) with one honest spinner
 * instead of a fake step list, and only real data in the done-stats grid
 * (workspace address, steps completed) — never a fabricated team/member
 * count this call never returns.
 *
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Onboarding Team Desktop.html §C6 "آماده‌سازی نهایی" /
 *   §C7 "ERP آماده است"
 * archetype: full-bleed centered transitional screen — spinner/success/
 *   failure icon, heading, real stat grid (done only), primary/secondary CTA
 * status: PARTIAL_REAL — the running state's per-step animated checklist
 *   (`provSteps`) is deliberately not built (see above); everything else
 *   (heading, real stats, CTAs, failure retry) is real.
 * state reference: running (request in flight) · done (real 200/201) ·
 *   failed (real error, message shown as-is)
 */
export function ProvisioningStep({
  phase,
  slug,
  stepsDone,
  stepsTotal,
  error,
  onRetry,
  onBackToReview,
  onEnter,
}: {
  phase: 'running' | 'done' | 'failed';
  slug: string;
  stepsDone: number;
  stepsTotal: number;
  error: string | null;
  onRetry: () => void;
  onBackToReview: () => void;
  onEnter: () => void;
}) {
  if (phase === 'running') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
        <V2Card>
          <div style={{ padding: '40px 48px', textAlign: 'center' }}>
            <LoadingState label="در حال آماده‌سازی ERP شما…" />
            <p style={{ margin: '18px 0 0', fontSize: 13, color: 'var(--sk-fg-subtle)' }}>
              صفحه را نبندید. پس از پایان، وارد فضای کاری می‌شوید.
            </p>
          </div>
        </V2Card>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 44 }}>
        <div
          style={{
            maxWidth: 520,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          <span
            style={{
              width: 46,
              height: 46,
              borderRadius: 23,
              background: 'var(--sk-danger-soft)',
              color: 'var(--sk-danger)',
              display: 'grid',
              placeContent: 'center',
            }}
          >
            <Icon name="x" size="md" />
          </span>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>راه‌اندازی کامل نشد</h1>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 2, color: 'var(--sk-fg-muted)' }}>
            {error ?? 'خطایی پیش آمد. دوباره تلاش کنید.'} تنظیمات و اعضای اضافه‌شده ذخیره شده‌اند و
            چیزی از بین نرفته است.
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <V2Button variant="primary" size="lg" onClick={onRetry}>
              تلاش مجدد
            </V2Button>
            <V2Button variant="ghost" size="lg" onClick={onBackToReview}>
              بازگشت به مرور
            </V2Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
      <div
        style={{ maxWidth: 620, width: '100%', display: 'flex', flexDirection: 'column', gap: 24 }}
      >
        <span
          style={{
            width: 50,
            height: 50,
            borderRadius: 25,
            background: 'var(--sk-success-soft)',
            color: 'var(--sk-success)',
            display: 'grid',
            placeContent: 'center',
          }}
        >
          <Icon name="check" size="lg" />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>ERP شما آماده است</h1>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 2, color: 'var(--sk-fg-muted)' }}>
            فضای کاری شما آماده استفاده است.
          </p>
        </div>
        <V2Card>
          <div
            style={{
              padding: 20,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11.5, color: 'var(--sk-fg-subtle)' }}>آدرس فضای کاری</span>
              <span style={{ fontSize: 15, fontWeight: 600, direction: 'ltr', textAlign: 'right' }}>
                cloud.sakhtar.net/t/{slug}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11.5, color: 'var(--sk-fg-subtle)' }}>
                بخش‌های تکمیل‌شده
              </span>
              <span style={{ fontSize: 15, fontWeight: 600 }}>
                {stepsDone.toLocaleString('fa-IR')} از {stepsTotal.toLocaleString('fa-IR')}
              </span>
            </div>
          </div>
        </V2Card>
        <div style={{ display: 'flex', gap: 12 }}>
          <V2Button variant="primary" size="lg" onClick={onEnter}>
            ورود به ERP
          </V2Button>
        </div>
      </div>
    </div>
  );
}
